import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const clearAuthData = useCallback(() => {
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user_data');
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const isValid = await authService.verifyToken();
          if (isValid) {
            setIsAuthenticated(true);
            // Récupère le profil complet (incl. theme_preference)
            try {
              const me = await authService.getMe();
              setUser(me);
            } catch {
              setUser(authService.getCurrentUser());
            }
          } else {
            clearAuthData();
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [clearAuthData]);

  const login = async (username, password) => {
    try {
      const result = await authService.login(username, password);
      if (result.success) {
        setIsAuthenticated(true);
        // Enrichir avec theme_preference via /me
        try {
          const me = await authService.getMe();
          setUser(me);
        } catch {
          setUser(result.user);
        }
        return { success: true };
      } else {
        return { success: false, error: result.error || 'Erreur de connexion' };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion'
      };
    }
  };

  /** Met à jour localement la préférence thème (post-sync DB). */
  const setUserTheme = useCallback((theme) => {
    setUser((prev) => (prev ? { ...prev, theme_preference: theme } : prev));
  }, []);

  /** Met à jour localement le prénom/nom (post-sync DB). */
  const setUserProfile = useCallback(({ first_name, last_name }) => {
    setUser((prev) => (prev ? { ...prev, first_name, last_name } : prev));
  }, []);

  const loginWithToken = async (token) => {
    sessionStorage.setItem('auth_token', token);
    try {
      const me = await authService.getMe();
      setIsAuthenticated(true);
      setUser(me);
      return me;
    } catch (error) {
      sessionStorage.removeItem('auth_token');
      throw error;
    }
  };

  const logout = () => {
    clearAuthData();
    navigate('/login', { replace: true });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        login,
        loginWithToken,
        logout,
        setUserTheme,
        setUserProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
